import React, { useEffect, useMemo, useRef } from 'react';
import { WMButton, WMButtonVariant, WMSelectOption } from '@walkme/wm-ui';
import { ReactComponent as PlusIcon } from '../../../../../assets/icons/plus-icon.svg';
import { createPropertyOptions, getUserRulesDictionary, integrationsToPropertyOptions, ruleTemplate, validateRuleComplete } from '../utils';
import Rule from './Rule/Rule';
import classes from './audienceRulesEditor.module.scss';
import { Controller, ControllerRenderProps, useFieldArray, useFormContext } from 'react-hook-form';
import { USER_IDENTIFIER_PROPERTY } from '../../../../state/reducers/audiences';
import { useIntegrations } from '../../hooks/integeations';
import { IntegrationFieldTypes } from '@walkme/audiences-types';
import { IntegrationGraphql } from '../../../../../generated/apollo-hooks';
import { AudienceFormFields } from '../CreateAudienceDialog';
import { useTranslation } from 'react-i18next';

const keyPrefix = 'components.audiences.createAudienceDialog.audienceRulesEditor.audienceRulesEditor';

export const AudienceRulesEditor = ({
    hasNotifications,
    calculateRecipientsSize,
    disabled = false,
    selectPropertyAutofocus
}: {
    hasNotifications?: boolean;
    calculateRecipientsSize: () => void;
    disabled?: boolean;
    selectPropertyAutofocus: boolean;
}) => {
    const { t } = useTranslation('', { keyPrefix });
    const { integrations } = useIntegrations();
    const { control, watch } = useFormContext<AudienceFormFields>();

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'rules'
    });

    const rules = watch('rules');

    const onDeleteRule = (index: number) => () => {
        remove(index);
        calculateRecipientsSize();
    };

    const propertyOptions = useMemo(
        () =>
            integrationsToPropertyOptions(
                [
                    {
                        fields: [
                            {
                                alias: 'ID',
                                name: USER_IDENTIFIER_PROPERTY,
                                dataType: IntegrationFieldTypes.String
                            }
                        ]
                    } as IntegrationGraphql,
                    ...(integrations || [])
                ],
                rules
            ),
        [integrations, rules]
    );

    const addButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        addButtonRef?.current?.scrollIntoView();
    }, [rules?.length]);

    const { rulesProperties, userRulesDictionary, isSingleRule, isValidRules } = useMemo(
        () => ({
            rulesProperties: createPropertyOptions(rules),
            userRulesDictionary: getUserRulesDictionary(rules),
            isSingleRule: rules.length === 1,
            isValidRules: validateRuleComplete(rules)
        }),
        [rules]
    );

    const onChangeProperty = (index: number, field: ControllerRenderProps<AudienceFormFields, 'rules'>) => async (property: WMSelectOption) => {
        field.onChange(
            field.value.map((rule: $TSFixMe, i: $TSFixMe) => {
                return i === index
                    ? {
                          ...rule,
                          ...property.raw,
                          values: []
                      }
                    : rule;
            })
        );
    };

    const onChangeValue = useMemo(
        () => (index: number, field: ControllerRenderProps<AudienceFormFields, 'rules'>) => async (values: WMSelectOption[]) => {
            field.onChange(
                field.value.map((rule: $TSFixMe, i: $TSFixMe) =>
                    i === index
                        ? {
                              ...rule,
                              values: values.map(({ value, amount }) => ({ name: value, amount }))
                          }
                        : rule
                )
            );
        },
        []
    );

    const onChangeOperator = (index: number, field: ControllerRenderProps<AudienceFormFields, 'rules'>) => async (value: WMSelectOption) => {
        field.onChange(
            field.value.map((rule, i) =>
                i === index
                    ? {
                          ...rule,

                          operator: value.value as $TSFixMe,
                          values: []
                      }
                    : rule
            )
        );
    };

    const isAddRuleDisabled = !isValidRules || rules?.length === propertyOptions?.length || disabled;
    return (
        <>
            {fields.map(({ id }, index) => {
                const property = rulesProperties[index];
                const propertyValues = property.value ? userRulesDictionary[property.integrationId!]?.[property.value] : [];
                return (
                    <Controller
                        key={id}
                        name={'rules'}
                        control={control}
                        render={({ field, formState: { errors } }) => {
                            return (
                                <Rule
                                    index={index}
                                    errors={errors.rules?.[index]}
                                    property={property}
                                    propertyOptions={propertyOptions}
                                    propertyValues={propertyValues}
                                    onChangeProperty={onChangeProperty(index, field)}
                                    onChangeValue={onChangeValue(index, field)}
                                    onChangeOperator={onChangeOperator(index, field)}
                                    onDeleteRule={onDeleteRule(index)}
                                    isSingleRule={isSingleRule}
                                    isFirstRule={index === 0}
                                    hasNotifications={!!hasNotifications}
                                    handleOnMenuClose={calculateRecipientsSize}
                                    selectPropertyAutofocus={selectPropertyAutofocus}
                                />
                            );
                        }}
                    />
                );
            })}

            {propertyOptions?.length > 0 && (
                <WMButton
                    ref={addButtonRef}
                    className={classes.newValueButton}
                    variant={WMButtonVariant.Text}
                    onClick={() => append(ruleTemplate)}
                    disabled={isAddRuleDisabled}
                    data-testid="createAudienceDialog-rules-add-rule"
                >
                    <PlusIcon className={classes.plus} />
                    {t('addNew')}
                </WMButton>
            )}
        </>
    );
};
